import * as yup from 'yup';
import { ProjectCandidateStageCategory } from 'generated/graphql';
import { STAGES_COLUMN_CATEGORIES } from '../../Projects.constants';
import { FiltersFormValues } from './ProjectsFilterModal';

export type FilterFieldName = keyof FiltersFormValues;

export enum AtsLinkedFilter {
  Any = 'ANY',
  Yes = 'YES',
  No = 'NO',
}

/**
 * The three states a stage row can be in. Only Has and None reach the API, as
 * a rule's `hasStageType` boolean; Any is the absence of a rule.
 */
export enum StageTypePresence {
  Any = 'ANY',
  Has = 'HAS',
  None = 'NONE',
}

/**
 * How far candidates must have got with the Online assessment. "Any" is the
 * absence of the rule rather than a value the API knows, the same way
 * {@link AtsLinkedFilter} handles its own Any.
 */
export enum AssessmentStatusFilter {
  Any = 'ANY',
  NotStarted = 'NOT_STARTED',
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
}

/**
 * The one stage type whose candidates carry an assessment status. An AI
 * interview is never included, so no other row offers the field.
 */
export const ASSESSMENT_STATUS_CATEGORY =
  ProjectCandidateStageCategory.OnlineAssessment;

/** The one stage type whose candidates can have an interview linked to them. */
export const INTERVIEW_LINKED_CATEGORY =
  ProjectCandidateStageCategory.InterviewStage;

/**
 * Whether the candidates counted must have an interview linked to the stage —
 * held or scheduled, the same thing the Candidates tooltip counts. Any is the
 * absence of the rule, as it is for {@link AtsLinkedFilter}.
 */
export enum InterviewLinkedFilter {
  Any = 'ANY',
  Yes = 'YES',
  No = 'NO',
}

export type StageTypeFilterValue = {
  presence: StageTypePresence;
  /** Kept as a string because it is bound to a text input. */
  minCandidates: string;
  /** Only offered on {@link ASSESSMENT_STATUS_CATEGORY}. */
  assessmentStatus: AssessmentStatusFilter;
  /** Only offered on {@link INTERVIEW_LINKED_CATEGORY}. */
  interviewLinked: InterviewLinkedFilter;
};

/** The five stage types the Stages tab can filter on. */
export type FilterableStageCategory =
  (typeof STAGES_COLUMN_CATEGORIES)[number];

export type StageTypeFilterValues = Record<
  FilterableStageCategory,
  StageTypeFilterValue
>;

/**
 * The stage types the Stages tab offers, in display order. The same five the
 * Stages column shows — there is no Offer row.
 */
export const STAGE_TYPE_FILTER_CATEGORIES = STAGES_COLUMN_CATEGORIES;

export const emptyStageTypeFilterValues = (): StageTypeFilterValues =>
  STAGE_TYPE_FILTER_CATEGORIES.reduce(
    (values, category) => ({
      ...values,
      [category]: {
        presence: StageTypePresence.Any,
        minCandidates: '',
        assessmentStatus: AssessmentStatusFilter.Any,
        interviewLinked: InterviewLinkedFilter.Any,
      },
    }),
    {} as StageTypeFilterValues
  );

const minCandidatesSchema = yup
  .string()
  .test(
    'minCandidates',
    'Must be a whole number of 1 or more',
    (value, { parent }) => {
      // Only meaningful alongside Has; the field is hidden otherwise, and
      // switching away from Has clears it.
      if (parent.presence !== StageTypePresence.Has) return true;
      if (!value) return true;
      return /^\d+$/.test(value) && Number(value) >= 1;
    }
  );

export const stageTypeFiltersValidationSchema = yup.object(
  STAGE_TYPE_FILTER_CATEGORIES.reduce(
    (schema, category) => ({
      ...schema,
      [category]: yup.object({
        presence: yup.string(),
        minCandidates: minCandidatesSchema,
        assessmentStatus: yup.string(),
        interviewLinked: yup.string(),
      }),
    }),
    {}
  )
);

/**
 * Fields that need more than "is it set" to be counted. Everything else falls
 * back to the generic check in `Projects.helpers`.
 */
export const FILTER_FIELD_COUNTERS: Partial<
  Record<FilterFieldName, (value: never) => number>
> = {
  atsLinked: (value: AtsLinkedFilter) =>
    value === AtsLinkedFilter.Any ? 0 : 1,
  // Each row that is not on Any is its own filter; neither the minimum nor the
  // assessment status nor the interview link adds to the count.
  stageTypeFilters: (value: StageTypeFilterValues) =>
    Object.values(value).filter(
      stageTypeFilter => stageTypeFilter.presence !== StageTypePresence.Any
    ).length,
};

/**
 * From/To pairs that read as one filter. Either end being set makes the pair
 * count once, which is what both the tab counters and the Filter button show.
 */
export const FILTER_RANGE_PAIRS: [FilterFieldName, FilterFieldName][] = [
  ['dateCreatedFrom', 'dateCreatedTo'],
  ['datePublishedFrom', 'datePublishedTo'],
  ['dateLastEditedFrom', 'dateLastEditedTo'],
  ['projectCandidatesFrom', 'projectCandidatesTo'],
];

export type ProjectsFilterTab = {
  key: string;
  title: string;
  fieldNames: FilterFieldName[];
};

/**
 * The tabs, in display order, and which filters live on each.
 *
 * This is the single source of truth for three things that have to agree: what
 * each tab renders, the count on its counter, and which tab Apply jumps to when
 * validation fails. Every field belongs to exactly one tab, so the tab counts
 * always add up to the total — `ProjectsFilterModal.helpers` has a test pinning
 * that.
 */
export const PROJECTS_FILTER_TABS: ProjectsFilterTab[] = [
  {
    key: 'role',
    title: 'Role',
    fieldNames: [
      'companies',
      'hiringTeamUsers',
      'status',
      'isArchived',
      'atsLinked',
      'projectCandidatesFrom',
      'projectCandidatesTo',
    ],
  },
  {
    key: 'stages',
    title: 'Stages',
    fieldNames: ['stageTypeFilters'],
  },
  {
    key: 'dates',
    title: 'Dates',
    fieldNames: [
      'dateCreatedFrom',
      'dateCreatedTo',
      'datePublishedFrom',
      'datePublishedTo',
      'dateLastEditedFrom',
      'dateLastEditedTo',
    ],
  },
];

export const STAGE_TYPE_PRESENCE_OPTIONS: {
  value: StageTypePresence;
  label: string;
}[] = [
  { value: StageTypePresence.Any, label: 'Any' },
  { value: StageTypePresence.Has, label: 'Has' },
  { value: StageTypePresence.None, label: 'None' },
];

export const ASSESSMENT_STATUS_OPTIONS: {
  value: AssessmentStatusFilter;
  label: string;
}[] = [
  { value: AssessmentStatusFilter.Any, label: 'Any' },
  { value: AssessmentStatusFilter.NotStarted, label: 'Not started' },
  { value: AssessmentStatusFilter.InProgress, label: 'In progress' },
  { value: AssessmentStatusFilter.Completed, label: 'Completed' },
];

export const INTERVIEW_LINKED_OPTIONS: {
  value: InterviewLinkedFilter;
  label: string;
}[] = [
  { value: InterviewLinkedFilter.Any, label: 'Any' },
  { value: InterviewLinkedFilter.Yes, label: 'Yes' },
  { value: InterviewLinkedFilter.No, label: 'No' },
];

export const ATS_LINKED_OPTIONS: { value: AtsLinkedFilter; label: string }[] = [
  { value: AtsLinkedFilter.Any, label: 'Any' },
  { value: AtsLinkedFilter.Yes, label: 'Yes' },
  { value: AtsLinkedFilter.No, label: 'No' },
];

export type { ProjectCandidateStageCategory };
